<div class="flex flex-col gap-6 animate-fade-in max-w-5xl mx-auto w-full">

  <!-- Header Section -->
  <div>
    <h1 class="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-[var(--text-muted)]">Settings</h1>
    <p class="text-[var(--text-muted)] mt-1">Configure your application, profile, and security preferences.</p>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mt-2">

    <!-- Sidebar Navigation -->
    <div class="md:col-span-1 flex flex-col gap-1">
      <button (click)="activeTab.set('profile')"
              class="settings-nav-btn"
              [class.settings-nav-btn--active]="activeTab() === 'profile'">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
        My Profile
      </button>
      <button (click)="activeTab.set('security')"
              class="settings-nav-btn"
              [class.settings-nav-btn--active]="activeTab() === 'security'">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
        Security
      </button>
      <div class="border-t border-[#ffffff08] my-2"></div>
      <button (click)="activeTab.set('distribution')"
              class="settings-nav-btn"
              [class.settings-nav-btn--active]="activeTab() === 'distribution'">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"/></svg>
        Distribution Rules
      </button>
      <button (click)="activeTab.set('cdn')"
              class="settings-nav-btn"
              [class.settings-nav-btn--active]="activeTab() === 'cdn'">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2"/></svg>
        CDN Configuration
      </button>
      <button (click)="activeTab.set('api-keys')"
              class="settings-nav-btn"
              [class.settings-nav-btn--active]="activeTab() === 'api-keys'">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/></svg>
        API Keys
      </button>
    </div>

    <!-- Main Content -->
    <div class="md:col-span-2">

      <!-- ══ PROFILE TAB ══ -->
      @if (activeTab() === 'profile') {
        <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl overflow-hidden">
          <div class="p-6 border-b border-[#ffffff10]">
            <h2 class="text-lg font-semibold text-white">My Profile</h2>
            <p class="text-sm text-[var(--text-muted)] mt-1">Update your personal information and profile photo.</p>
          </div>

          @if (isLoadingProfile()) {
            <div class="flex items-center justify-center py-16 gap-3 text-[var(--text-muted)]">
              <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
              Loading profile…
            </div>
          }

          @if (!isLoadingProfile()) {
            <div class="p-6 flex flex-col gap-6">
              <!-- Avatar -->
              <div class="flex items-center gap-5">
                <div class="relative w-20 h-20 flex-shrink-0">
                  <div class="w-20 h-20 rounded-full bg-gradient-to-tr from-purple-500 to-[#506ef7] flex items-center justify-center font-bold text-2xl text-white shadow-lg ring-4 ring-[#ffffff10] overflow-hidden">
                    @if (currentUser()?.profile_photo_url) {
                      <img [src]="currentUser()!.profile_photo_url" class="w-full h-full object-cover" alt="Profile">
                    } @else {
                      {{ getInitials() }}
                    }
                  </div>
                  @if (photoUploading()) {
                    <div class="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                      <svg class="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                    </div>
                  }
                </div>
                <div>
                  <p class="text-white font-medium">{{ currentUser()?.first_name }} {{ currentUser()?.last_name }}</p>
                  <p class="text-sm text-[var(--text-muted)] mb-3">{{ currentUser()?.email }}</p>
                  <label class="cursor-pointer">
                    <input type="file" accept="image/*" class="hidden" (change)="onPhotoSelected($event)">
                    <span class="px-4 py-1.5 text-sm font-medium rounded-lg border border-[#ffffff15] text-[var(--text-muted)] hover:text-white hover:border-[#ffffff30] transition-all bg-[#ffffff05] hover:bg-[#ffffff08]">
                      Change Photo
                    </span>
                  </label>
                </div>
              </div>

              <!-- Profile Form -->
              <form [formGroup]="profileForm" class="flex flex-col gap-4">
                <div class="grid grid-cols-2 gap-4">
                  <div class="settings-form-group">
                    <label class="settings-form-label">First Name</label>
                    <input formControlName="first_name" type="text" class="settings-form-input"
                           [class.border-red-500]="profileForm.get('first_name')?.invalid && profileForm.get('first_name')?.touched">
                  </div>
                  <div class="settings-form-group">
                    <label class="settings-form-label">Last Name</label>
                    <input formControlName="last_name" type="text" class="settings-form-input"
                           [class.border-red-500]="profileForm.get('last_name')?.invalid && profileForm.get('last_name')?.touched">
                  </div>
                </div>
                <div class="settings-form-group">
                  <label class="settings-form-label">Email Address</label>
                  <input formControlName="email" type="email" class="settings-form-input"
                         [class.border-red-500]="profileForm.get('email')?.invalid && profileForm.get('email')?.touched">
                </div>

                @if (profileError()) {
                  <div class="settings-alert-error">{{ profileError() }}</div>
                }
                @if (profileSuccess()) {
                  <div class="settings-alert-success">{{ profileSuccess() }}</div>
                }
              </form>
            </div>

            <div class="p-6 border-t border-[#ffffff10] bg-[#ffffff02] flex justify-end gap-3">
              <button class="px-5 py-2 rounded-lg text-white hover:bg-[#ffffff10] transition-colors font-medium text-sm" (click)="loadProfile()">Cancel</button>
              <button (click)="saveProfile()" [disabled]="profileSaving()"
                      class="px-5 py-2 rounded-lg bg-[#506ef7] hover:bg-[#4057db] text-white font-medium text-sm shadow-[0_4px_14px_0_rgba(80,110,247,0.39)] transition-all disabled:opacity-50 flex items-center gap-2">
                @if (profileSaving()) {
                  <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                }
                Save Changes
              </button>
            </div>
          }
        </div>
      }

      <!-- ══ SECURITY TAB ══ -->
      @if (activeTab() === 'security') {
        <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl overflow-hidden">
          <div class="p-6 border-b border-[#ffffff10]">
            <h2 class="text-lg font-semibold text-white">Change Password</h2>
            <p class="text-sm text-[var(--text-muted)] mt-1">Keep your account secure with a strong password.</p>
          </div>

          <div class="p-6">
            <form [formGroup]="passwordForm" class="flex flex-col gap-4">
              <div class="settings-form-group">
                <label class="settings-form-label">Current Password</label>
                <input formControlName="current_password" type="password" class="settings-form-input" placeholder="Enter current password"
                       [class.border-red-500]="passwordForm.get('current_password')?.invalid && passwordForm.get('current_password')?.touched">
              </div>
              <div class="settings-form-group">
                <label class="settings-form-label">New Password</label>
                <input formControlName="new_password" type="password" class="settings-form-input" placeholder="Min. 6 characters"
                       [class.border-red-500]="passwordForm.get('new_password')?.invalid && passwordForm.get('new_password')?.touched">
                @if (passwordForm.get('new_password')?.errors?.['minlength'] && passwordForm.get('new_password')?.touched) {
                  <span class="text-xs text-red-400">Minimum 6 characters</span>
                }
              </div>
              <div class="settings-form-group">
                <label class="settings-form-label">Confirm New Password</label>
                <input formControlName="confirm_password" type="password" class="settings-form-input" placeholder="Repeat new password"
                       [class.border-red-500]="passwordForm.get('confirm_password')?.invalid && passwordForm.get('confirm_password')?.touched">
              </div>

              @if (passwordError()) {
                <div class="settings-alert-error">{{ passwordError() }}</div>
              }
              @if (passwordSuccess()) {
                <div class="settings-alert-success">{{ passwordSuccess() }}</div>
              }
            </form>
          </div>

          <div class="p-6 border-t border-[#ffffff10] bg-[#ffffff02] flex justify-end gap-3">
            <button class="px-5 py-2 rounded-lg text-white hover:bg-[#ffffff10] transition-colors font-medium text-sm" (click)="passwordForm.reset()">Clear</button>
            <button (click)="savePassword()" [disabled]="passwordSaving()"
                    class="px-5 py-2 rounded-lg bg-[#506ef7] hover:bg-[#4057db] text-white font-medium text-sm shadow-[0_4px_14px_0_rgba(80,110,247,0.39)] transition-all disabled:opacity-50 flex items-center gap-2">
              @if (passwordSaving()) {
                <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
              }
              Change Password
            </button>
          </div>
        </div>
      }

      <!-- ══ DISTRIBUTION TAB ══ -->
      @if (activeTab() === 'distribution') {
        <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl overflow-hidden">
          <div class="p-6 border-b border-[#ffffff10]">
            <h2 class="text-lg font-semibold text-white">Active Regions</h2>
            <p class="text-sm text-[var(--text-muted)] mt-1">Enable or disable content delivery to specific geographical areas.</p>
          </div>
          <div class="p-6 flex flex-col gap-6">
            @for (region of regions; track region.id) {
              <div class="flex items-start justify-between p-4 rounded-lg bg-[#ffffff05] border border-[#ffffff0a] hover:border-[#ffffff1a] transition-colors group">
                <div class="flex flex-col gap-1">
                  <h3 class="text-white font-medium group-hover:text-[#506ef7] transition-colors">{{ region.name }}</h3>
                  <div class="flex items-center gap-4 text-xs text-[var(--text-muted)]">
                    <span class="flex items-center gap-1">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path></svg>
                      {{ region.language }}
                    </span>
                    <span class="flex items-center gap-1">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      {{ region.timezone }}
                    </span>
                  </div>
                </div>
                <button (click)="toggleRegion(region)"
                        class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                        [class]="region.active ? 'bg-[#506ef7]' : 'bg-gray-600'">
                  <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                        [class]="region.active ? 'translate-x-5' : 'translate-x-0'"></span>
                </button>
              </div>
            }

            <hr class="border-[#ffffff10] my-2">
            <h2 class="text-lg font-semibold text-white">Language Support</h2>
            <p class="text-sm text-[var(--text-muted)] -mt-4 mb-2">Select the languages in which content will be generated and distributed.</p>
            <div class="flex flex-wrap gap-3">
              @for (lang of availableLanguages; track lang.code) {
                <button (click)="toggleLanguage(lang)"
                        class="px-4 py-2 rounded-lg border transition-all duration-300 text-sm font-medium flex items-center gap-2"
                        [class]="lang.selected ? 'bg-[#506ef7]/10 border-[#506ef7]/50 text-[#506ef7] shadow-[0_0_12px_rgba(80,110,247,0.15)]' : 'bg-[#ffffff05] border-[#ffffff10] text-[var(--text-muted)] hover:border-[#ffffff20] hover:text-white'">
                  @if (lang.selected) {
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  }
                  {{ lang.name }}
                </button>
              }
            </div>

            <hr class="border-[#ffffff10] my-2 mt-4">
            <h2 class="text-lg font-semibold text-white">Advanced Rules</h2>
            <div class="flex flex-col gap-4">
              <div>
                <label class="block text-sm font-medium text-white mb-1.5">Default Fallback Region</label>
                <select class="block w-full px-4 py-2.5 bg-[#ffffff05] border border-[#ffffff10] rounded-lg text-white focus:outline-none focus:border-[#506ef7] focus:ring-1 focus:ring-[#506ef7] transition-all appearance-none cursor-pointer">
                  <option value="na">North America (NA)</option>
                  <option value="emea">Europe, Mid-East, Africa (EMEA)</option>
                  <option value="apac">Asia-Pacific (APAC)</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-white mb-1.5">Cache Expiration (TTL)</label>
                <div class="flex items-center gap-3">
                  <input type="range" class="w-full h-1.5 bg-[#ffffff10] rounded-lg appearance-none cursor-pointer accent-[#506ef7]" min="1" max="72" value="24">
                  <span class="text-white font-medium min-w-[3rem] text-right">24h</span>
                </div>
              </div>
            </div>
          </div>
          <div class="p-6 border-t border-[#ffffff10] bg-[#ffffff02] flex justify-end gap-3">
            <button class="px-5 py-2 rounded-lg text-white hover:bg-[#ffffff10] transition-colors font-medium">Cancel</button>
            <button class="px-5 py-2 rounded-lg bg-[#506ef7] hover:bg-[#4057db] text-white font-medium shadow-[0_4px_14px_0_rgba(80,110,247,0.39)] hover:shadow-[0_6px_20px_rgba(80,110,247,0.23)] transition-all">Save Changes</button>
          </div>
        </div>
      }

      <!-- ══ CDN / API KEYS placeholder tabs ══ -->
      @if (activeTab() === 'cdn' || activeTab() === 'api-keys') {
        <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-12 flex flex-col items-center justify-center text-center">
          <svg class="w-10 h-10 text-[#ffffff20] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/></svg>
          <p class="text-[var(--text-muted)] font-medium">Coming soon</p>
          <p class="text-sm text-[var(--text-muted)] opacity-60 mt-1">This section is under construction.</p>
        </div>
      }

    </div>
  </div>
</div>
